/* @flow */

import * as React from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'

import {
  addPxToInput,
  ensureAbsoluteUrl,
  removeNonDigits,
} from '../../../../shared'
import {
  useContainerSize,
  useSizeCacheClearningOnlyOnBiggerChanges,
  useParsedMediaFile,
  getDimensionCss,
  WidgetContainer,
  getDimensionValue,
} from '../shared'
import {
  WebshopContext,
  NAVIGATION_LINK_TYPE_SEARCH,
  NAVIGATION_LINK_TYPE_PAGE,
  NAVIGATION_LINK_TYPE_PAGE_V2,
  NAVIGATION_LINK_TYPE_BESTSELLERS,
} from '../../../shared'

const WebshopBanner = ({ brand, gridItem, ...rest }) => {
  const { navigationLinks } = React.useContext(WebshopContext)

  const navigationLinksIdToUrlMap = React.useMemo(() => {
    return navigationLinks.reduce((acc, link) => {
      let url = ''

      if (
        link.type === NAVIGATION_LINK_TYPE_SEARCH ||
        link.type === NAVIGATION_LINK_TYPE_BESTSELLERS
      ) {
        url = `/shop/${brand.id}/search/${link.id}`
      } else if (
        link.type === NAVIGATION_LINK_TYPE_PAGE ||
        link.type === NAVIGATION_LINK_TYPE_PAGE_V2
      ) {
        url = `/shop/${brand.id}/page/${link.settings.page_id}`
      }

      acc[link.id] = url
      return acc
    }, {})
  }, [navigationLinks])

  const contentContainerStyle =
    useWebshopGridItemBannerContentContainerStyle(gridItem)

  const heightValue = getDimensionValue(
    gridItem.data.height_mode,
    gridItem.data.fixed_height,
    null,
    gridItem.data.viewport_percentage_height
  )

  return (
    <BannerLinkWrap
      brand={brand}
      gridItem={gridItem}
      navigationLinksIdToUrlMap={navigationLinksIdToUrlMap}
    >
      <WebshopGridItemBanner
        bodyColor={gridItem.data.body_font_color}
        contentContainerStyle={contentContainerStyle}
        gridItem={gridItem}
        heightMode={gridItem.data.height_mode}
        heightValue={heightValue}
        imageOrVideo={gridItem.data.banner}
      >
        <WebshopGridItemBannerChildrenContainer gridItem={gridItem}>
          {gridItem.children.map(childGridItem => {
            const GridItemComponent = childGridItem.component

            if (!GridItemComponent) {
              return null
            }

            return <GridItemComponent gridItem={childGridItem} {...rest} />
          })}
        </WebshopGridItemBannerChildrenContainer>
      </WebshopGridItemBanner>
    </BannerLinkWrap>
  )
}

export default WebshopBanner

const Container = styled(WidgetContainer)`
  color: ${({ bodyColor }) => bodyColor || 'inherit'};
  position: relative;

  ${props => getDimensionCss('height', props.heightValue)}
`

const BackgroundContainer = styled.div`
  top: 0;
  left: 0;
  right: 0;
  min-width: 100%;
  position: absolute;
  z-index: -100;

  ${props => getDimensionCss('height', props.heightValue)}
`

const VideoComponent = styled.video`
  display: block;
  width: 100%;
  top: 50%;
  object-fit: cover;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -50%);

  ${props => getDimensionCss('height', props.heightValue)}
`

const ImageComponent = styled.img`
  display: block;
  top: 50%;
  object-fit: cover;
  left: 50%;
  position: absolute;
  transform: translate(-50%, -50%);
  width: 100%;

  ${props => getDimensionCss('height', props.heightValue)}
`

const BackgroundPlaceholder = styled.div`
  width: 100%;
  background-color: rgba(213, 213, 213, 0.8);

  ${props => getDimensionCss('height', props.heightValue)}
`

const ContentContainer = styled.div`
  position: relative;
`

const BannerLinkWrap = ({
  brand,
  children,
  gridItem,
  navigationLinksIdToUrlMap,
}) => {
  if (
    gridItem.data.link_type === 'navigation_link' &&
    gridItem.data.navigation_link_id &&
    navigationLinksIdToUrlMap[gridItem.data.navigation_link_id]
  ) {
    return (
      <Link to={navigationLinksIdToUrlMap[gridItem.data.navigation_link_id]}>
        {children}
      </Link>
    )
  }

  if (gridItem.data.link_type === 'webshop_page') {
    return (
      <Link
        to={
          gridItem.data.webshop_page_id
            ? `/shop/${brand.id}/page/${gridItem.data.webshop_page_id}`
            : ''
        }
      >
        {children}
      </Link>
    )
  }

  if (gridItem.data.link_type === 'manual_url') {
    return <a href={ensureAbsoluteUrl(gridItem.data.manual_url)}>{children}</a>
  }

  return children
}

type WebshopGridItemBannerProps = {
  bodyColor?: string,
  children: React.Node,
  contentContainerStyle: Object,
  gridItem: Object,
  heightMode: 'viewport_percentage' | 'keep_image_aspect_ratio' | 'fixed',
  heightValue: number,
  imageOrVideo: string | Array<{ path: string }> | null,
  isEditing?: boolean,
}

export const WebshopGridItemBanner = ({
  bodyColor,
  children,
  className,
  contentContainerStyle,
  gridItem,
  heightMode,
  heightValue,
  imageOrVideo,
  isEditing,
  onClick,
}: WebshopGridItemBannerProps) => {
  const containerRef = React.useRef(null)
  const containerSize = useContainerSize(containerRef)
  const cachedContainerSize =
    useSizeCacheClearningOnlyOnBiggerChanges(containerSize)

  const { imageUrl, videoSources } = useParsedMediaFile(
    imageOrVideo,
    cachedContainerSize
  )

  return (
    <Container
      bodyColor={bodyColor}
      className={className}
      gridItem={gridItem}
      heightMode={heightMode}
      heightValue={heightValue}
      onClick={onClick}
      ref={containerRef}
    >
      <BackgroundContainer heightMode={heightMode} heightValue={heightValue}>
        {videoSources !== null && (
          <VideoComponent
            autoPlay
            playsinline
            loop
            muted
            heightMode={heightMode}
            heightValue={heightValue}
          >
            {videoSources.map(source => (
              <source key={source.url} src={source.url} type={source.mime} />
            ))}
          </VideoComponent>
        )}
        {videoSources === null && imageUrl !== null && (
          <ImageComponent
            src={imageUrl}
            heightMode={heightMode}
            heightValue={heightValue}
          />
        )}
        {videoSources === null && imageUrl === null && isEditing && (
          <BackgroundPlaceholder
            heightMode={heightMode}
            heightValue={heightValue}
          />
        )}
      </BackgroundContainer>

      <ContentContainer style={contentContainerStyle}>
        {children}
      </ContentContainer>
    </Container>
  )
}

export const useWebshopGridItemBannerContentContainerStyle = gridItem => {
  const contentContainerStyle = React.useMemo(() => {
    const style = {
      height: '100%',
    }

    if (gridItem.data.inner_elements_position === 'center') {
      style.alignItems = 'center'
      style.display = 'flex'
      style.justifyContent = 'center'
    }

    return style
  }, [gridItem.data.inner_elements_position])

  return contentContainerStyle
}

export const WebshopGridItemBannerChildrenContainer = styled.div`
  display: flex;
  flex-direction: ${({ gridItem }) =>
    gridItem.data.inner_elements_stack_mode === 'stacked' ? 'column' : 'row'};
  margin-left: ${({ gridItem }) =>
    gridItem.data.inner_elements_stack_mode === 'side_by_side'
      ? addPxToInput(
          -removeNonDigits(gridItem.data.inner_elements_space_between_each)
        )
      : 0};
  position: ${({ gridItem }) =>
    gridItem.data.inner_elements_position === 'center'
      ? 'relative'
      : 'absolute'};

  ${({ gridItem }) => {
    if (gridItem.data.inner_elements_position === 'top_left') {
      return css`
        left: 0;
        top: 0;
      `
    } else if (gridItem.data.inner_elements_position === 'top_right') {
      return css`
        right: 0;
        top: 0;
      `
    } else if (gridItem.data.inner_elements_position === 'bottom_left') {
      return css`
        bottom: 0;
        left: 0;
      `
    } else if (gridItem.data.inner_elements_position === 'bottom_right') {
      return css`
        bottom: 0;
        right: 0;
      `
    }
  }};

  > * {
    margin-bottom: ${({ gridItem }) =>
      gridItem.data.inner_elements_stack_mode === 'stacked'
        ? addPxToInput(gridItem.data.inner_elements_space_between_each)
        : 0};
    margin-left: ${({ gridItem }) =>
      gridItem.data.inner_elements_stack_mode === 'side_by_side'
        ? addPxToInput(gridItem.data.inner_elements_space_between_each)
        : 0};
  }
`
